/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useMemo, useState, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import { change, clearSubmitErrors, reduxForm } from 'redux-form';
import CompanyAboutContent from './components/company-about-content';
import saga from '../../saga';
import reducer from '../../reducer';
import { useInjectReducer } from '../../../../../utils/injectReducer';
import { useInjectSaga } from '../../../../../utils/injectSaga';
import {
	getIsCompanyAboutLoadingSelector,
	getCompaniesModeSelector,
	getCompaniesTonnageSelector,
	getCompaniesDeliverySelector,
	getCompaniesCarSelector,
	getIsModesTypesLoadingSelector,
	getIsTonnagesTypesLoadingSelector,
	getIsDeliveryTypesLoadingSelector,
	getIsCarTypesLoadingSelector,
	getFormInitialCompanyAboutValuesSelector,
	getFormCompaniesAboutValuesSelector,
} from '../../selectors';
import { CompanyFormView } from './components/company-about-form';
import { FORM_ABOUT_NAME } from '../../constants';
import { validateAbout } from '../../utils';

const CompanyAboutWrapperForm = ({
	isCompanyInfoLoading,
	companyAboutInfo,
	modes,
	tonnages,
	deliveryMethods,
	carTypes,
	isModesTypesLoading,
	isTonnagesTypesLoading,
	isDeliveryTypesLoading,
	isCarTypesLoading,
	onChangeField,
	formValues,
	initialValues,
	clearError,
}) => {
	useInjectSaga({ key: 'companiesModalSaga', saga });
	useInjectReducer({ key: 'companiesModalReducer', reducer });

	const { search } = useLocation();

	const query = new URLSearchParams(search);

	const mode = query.get('mode');

	const isEditing = mode === 'about-company-edit';

	const [carFleetRows, setCarFleetRows] = useState([
		{
			id: 0,
			car_type_id: null,
			tonnage_id: null,
			quantity: null,
		},
	]);

	useEffect(() => {
		if (isEditing) {
			const transportsData = formValues?.transports || initialValues?.transports;

			if (transportsData && transportsData.length > 0) {
				const initialRows = transportsData.map((transport, index) => ({
					id: transport.id || index,
					car_type_id: transport.car_type_id,
					tonnage_id: transport.tonnage_id,
					quantity: transport.quantity,
				}));

				setCarFleetRows(initialRows);
			}
		}
	}, [isEditing, initialValues, formValues]);

	const isOptionsListLoading = useMemo(
		() => ({
			deliveryMethods: isDeliveryTypesLoading,
			carTypes: isCarTypesLoading,
			tonnages: isTonnagesTypesLoading,
			temperatureModes: isModesTypesLoading,
		}),
		[isDeliveryTypesLoading, isCarTypesLoading, isTonnagesTypesLoading, isModesTypesLoading],
	);

	const handleAddCarTypeButtonClick = () => {
		const newIndex = carFleetRows.length;

		const newRow = {
			id: Date.now(),
			car_type_id: null,
			tonnage_id: null,
			quantity: null,
		};

		const updatedRows = [...carFleetRows, newRow];
		setCarFleetRows(updatedRows);

		onChangeField(FORM_ABOUT_NAME, `transports[${newIndex}].car_type_id`, null);
		onChangeField(FORM_ABOUT_NAME, `transports[${newIndex}].tonnage_id`, null);
		onChangeField(FORM_ABOUT_NAME, `transports[${newIndex}].quantity`, null);
	};

	const handleFieldChange = (fieldName, index) => {
		clearError(FORM_ABOUT_NAME, `transports[${index}].${fieldName}`);

		clearError(FORM_ABOUT_NAME, 'transports._error');
	};

	const handleRemoveCarRow = index => {
		if (index === 0 && carFleetRows.length === 1) {
			onChangeField(FORM_ABOUT_NAME, 'transports[0].car_type_id', null);
			onChangeField(FORM_ABOUT_NAME, 'transports[0].tonnage_id', null);
			onChangeField(FORM_ABOUT_NAME, 'transports[0].quantity', null);

			const clearedRow = {
				...carFleetRows[0],
				car_type_id: null,
				tonnage_id: null,
				quantity: null,
			};

			setCarFleetRows([clearedRow]);
		} else {
			const updatedRows = carFleetRows.slice(0, -1);
			setCarFleetRows(updatedRows);

			const currentTransports = formValues?.transports || [];

			const newTransports = currentTransports.slice(0, -1);

			onChangeField(FORM_ABOUT_NAME, 'transports', newTransports);
		}
	};

	if (isEditing) {
		return (
			<CompanyFormView
				isOptionsListLoading={isOptionsListLoading}
				deliveryMethods={deliveryMethods}
				carTypes={carTypes}
				tonnages={tonnages}
				temperatureModes={modes}
				carFleetRows={carFleetRows}
				onAddCarTypeButtonClick={handleAddCarTypeButtonClick}
				onRemoveCarRow={handleRemoveCarRow}
				handleChange={handleFieldChange}
			/>
		);
	}

	return <CompanyAboutContent companyAboutInfo={companyAboutInfo} isCompanyInfoLoading={isCompanyInfoLoading} />;
};

const withForm = reduxForm({
	form: FORM_ABOUT_NAME,
	enableReinitialize: true,
	onSubmit: validateAbout,
	onChange: (_, dispatch) => {
		dispatch(clearSubmitErrors(FORM_ABOUT_NAME));
	},
})(CompanyAboutWrapperForm);

const mapStateToProps = createStructuredSelector({
	isCompanyInfoLoading: getIsCompanyAboutLoadingSelector(),
	formValues: getFormCompaniesAboutValuesSelector(),
	modes: getCompaniesModeSelector(),
	tonnages: getCompaniesTonnageSelector(),
	deliveryMethods: getCompaniesDeliverySelector(),
	carTypes: getCompaniesCarSelector(),
	isModesTypesLoading: getIsModesTypesLoadingSelector(),
	isTonnagesTypesLoading: getIsTonnagesTypesLoadingSelector(),
	isDeliveryTypesLoading: getIsDeliveryTypesLoadingSelector(),
	isCarTypesLoading: getIsCarTypesLoadingSelector(),
	initialValues: getFormInitialCompanyAboutValuesSelector(),
});

const mapDispatchToProps = {
	onChangeField: change,
	clearError: clearSubmitErrors,
};

const CompanyAbout = connect(mapStateToProps, mapDispatchToProps)(withForm);

CompanyAboutWrapperForm.propTypes = {
	companyAboutInfo: PropTypes.array,
	onChangeField: PropTypes.func.isRequired,
	isCompanyInfoLoading: PropTypes.bool.isRequired,
	formValues: PropTypes.object,
	initialValues: PropTypes.object,
	modes: PropTypes.array,
	tonnages: PropTypes.array,
	deliveryMethods: PropTypes.array,
	isModesTypesLoading: PropTypes.bool.isRequired,
	isTonnagesTypesLoading: PropTypes.bool.isRequired,
	isDeliveryTypesLoading: PropTypes.bool.isRequired,
	isCarTypesLoading: PropTypes.bool.isRequired,
	carTypes: PropTypes.array,
	clearError: PropTypes.func.isRequired,
};

export default CompanyAbout;
